import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['menu', 'submenu']
  static values = {
    id: String,
    variation: String
  }

  connect() {
    if (!this.idValue) {
      this.idValue = Math.random().toString(36).substring(2, 15);
    }
    this.submenuStates = {}
    this.submenuStates[this.idValue] = {};
    this.showSubmenu = this.debounce(this.showSubmenu.bind(this), 200);
    this.hideSubmenu = this.debounce(this.hideSubmenu.bind(this), 200);
    this.handleResize();

    // Add resize event listener
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  disconnect() {
    // Remove resize event listener
    window.removeEventListener('resize', this.handleResize.bind(this));
  }

  toggleSubmenu(event) {
    if (this.variationValue === 'sidebar-accordion' || this.isHamburgerVisible()) {
      if (event.type !== 'click' && event.type !== 'touchstart') {
        return;
      }
    }

    const target = event.currentTarget;
    const targetSubmenu = target.parentElement.querySelector('[data-menu-target="submenu"]');
    const button = target.querySelector('.menu__toggle--with-sub');
    const index = this.submenuTargets.indexOf(targetSubmenu);

    if (!this.submenuStates[this.idValue][index]) {
      this.submenuStates[this.idValue][index] = { lastAction: null, hideTimeout: null };
    }

    const state = this.submenuStates[this.idValue][index];

    if (state.lastAction === 'hide') {
      clearTimeout(state.hideTimeout);
    }

    if (button.getAttribute('aria-expanded') === 'true') {
      this.hideSubmenu(event);
    } else {
      this.showSubmenu(event);
    }
  }

  showSubmenu(event) {
    if (this.variationValue === 'sidebar-accordion' || this.isHamburgerVisible()) {
      if (event.type !== 'click' && event.type !== 'touchstart') {
        return;
      }
    }

    let target = event.target;
    while (target.tagName !== 'LI') {
      target = target.parentElement;
    }

    const targetSubmenu = target.querySelector('[data-menu-target="submenu"]');
    const button = target.querySelector('.menu__toggle--with-sub');
    const index = this.submenuTargets.indexOf(targetSubmenu);

    if (!this.submenuStates[this.idValue][index]) {
      this.submenuStates[this.idValue][index] = { lastAction: null, hideTimeout: null };
    }

    const state = this.submenuStates[this.idValue][index];

    if (state.lastAction === 'hide') {
      clearTimeout(state.hideTimeout);
    }
    button.setAttribute('aria-expanded', 'true');
    state.lastAction = 'show';
    targetSubmenu.classList.remove('invisible', 'submenu-close');
    targetSubmenu.classList.add('submenu-open');
  }

  hideSubmenu(event) {
    if (this.variationValue === 'sidebar-accordion' || this.isHamburgerVisible()) {
      if (event.type !== 'click' && event.type !== 'touchstart') {
        return;
      }
    }

    let target = event.target;
    while (target.tagName !== 'LI') {
      target = target.parentElement;
    }

    const targetSubmenu = target.querySelector('[data-menu-target="submenu"]');
    const button = target.querySelector('.menu__toggle--with-sub');
    const index = this.submenuTargets.indexOf(targetSubmenu);

    if (!this.submenuStates[this.idValue][index]) {
      this.submenuStates[this.idValue][index] = { lastAction: null, hideTimeout: null };
    }

    const state = this.submenuStates[this.idValue][index];

    if (state.lastAction === 'show') {
      clearTimeout(state.showTimeout);
    }
    button.setAttribute('aria-expanded', 'false');
    state.lastAction = 'hide';
    targetSubmenu.classList.remove('submenu-open');
    targetSubmenu.classList.add('submenu-close');
    state.hideTimeout = setTimeout(() => {
      if (state.lastAction === 'hide') {
        targetSubmenu.classList.add('invisible');
      }
    }, 300);
  }

  toggleMenu(event) {
    const hamburger = event.currentTarget;
    const isExpanded = hamburger.getAttribute("aria-expanded") === "true";

    hamburger.setAttribute("aria-expanded", !isExpanded);
    if (isExpanded) {
      this.menuTargets.forEach((menu) => {
        menu.style.height = menu.scrollHeight + 'px';
        setTimeout(() => {
          menu.style.height = '0px';
        }, 100);
        setTimeout(() => {
          menu.hidden = true;
        }, 300);
      });
    } else {
      this.menuTargets.forEach((menu) => {
        menu.hidden = false;
        menu.style.height = menu.scrollHeight + 'px';
        setTimeout(() => {
          menu.setAttribute('style', '');
        }, 300);
      });
    }
  }

  handleResize() {
    this.menuTargets.forEach((menu) => {
      const hamburger = menu.parentElement.querySelector('.menu__hamburger');

      if (hamburger) {
        const compStyles = window.getComputedStyle(hamburger);
        if (compStyles.display === 'none') {
          menu.setAttribute('style', '');
        }
      }
    });
  }

  isHamburgerVisible() {
    const hamburger = this.menuTargets[0].parentElement.querySelector('.menu__hamburger');
    const compStyles = window.getComputedStyle(hamburger);
    return compStyles.display !== 'none';
  }

  debounce(fn, delay) {
    return function (...args) {
      const targetSubmenu = args[0].target.querySelector('[data-menu-target="submenu"]');
      const index = this.submenuTargets.indexOf(targetSubmenu);

      if (!this.submenuStates[this.idValue][index]) {
        this.submenuStates[this.idValue][index] = { lastActionTimeout: null };
      }

      const state = this.submenuStates[this.idValue][index];

      if (state.lastActionTimeout) {
        clearTimeout(state.lastActionTimeout);
      }
      state.lastActionTimeout = setTimeout(() => {
        fn.apply(this, args);
      }, delay);
    }.bind(this);
  }
}
